import mitt from 'mitt';
import { isCancel } from 'axios';

const toastBus = mitt();

const TOAST_LIFE_MS = 2300;

export const onToast = (handler) => toastBus.on('show', handler);
export const offToast = (handler) => toastBus.off('show', handler);

const CONNECTION_ERRORS = {
  ECONNABORTED: ['connAborted', 'This is taking longer than expected. Please try again.'],
  ECONNREFUSED: ['connRefused', 'We couldn’t connect to the service. Please try again shortly.'],
  ECONNRESET: ['connReset', 'We couldn’t connect to the service. Please try again shortly.'],
  ETIMEDOUT: ['timedOut', 'This is taking longer than expected. Please try again.'],
  EHOSTUNREACH: [
    'hostUnreachable',
    'We couldn’t connect to the service. Please try again shortly.',
  ],
  ENOTFOUND: ['notFound', 'We couldn’t connect to the service. Please try again shortly.'],
};

class ToastManager {
  translate = null;

  setTranslator(translate) {
    this.translate = translate;
  }

  t(key, fallback) {
    const translation = this.translate?.(key);
    return translation && translation !== key ? translation : fallback;
  }

  show(severity, message, options = {}) {
    toastBus.emit('show', {
      key: options.key,
      icon: options.icon,
      severity,
      summary: options.summary ?? '',
      detail: message,
      life: options.life ?? TOAST_LIFE_MS,
    });
  }

  success(message, options) {
    this.show('success', message, options);
  }

  info(message, options) {
    this.show('info', message, options);
  }

  warn(message, options) {
    this.show('warn', message, options);
  }

  error(message, options) {
    this.show('error', message, options);
  }

  exception(error) {
    if (isCancel(error)) {
      return;
    }
    if (!(error instanceof Error)) {
      this.warn(
        this.t(
          'common.error.unexpectedConnection',
          'We couldn’t connect. Please check your internet connection and try again.',
        ),
      );
      return;
    }

    const [key, message] = CONNECTION_ERRORS[error.code] ?? [
      'unexpected',
      'We couldn’t complete this action. Please try again.',
    ];
    this.error(this.t(`common.error.${key}`, message));
  }
}

export const ToastService = new ToastManager();
